import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { TableHeaderFilter } from "@lib/types/Api";
import { useMemo } from "react";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableProps } from "antd";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import { AppContext, ScannedTransaction, SharedContextProps } from "@lib/types";
import { useQuery } from "@tanstack/react-query";
import { apiGetBillingScannedTransaction } from "@lib/api/wallet";
import useScreen from "@lib/hooks/useScreen";
import getColumns from "./columns";

const SharedBlockchainPaymentList: React.FC<SharedContextProps> = ({
  context,
  customerId,
  customTitle,
  withoutContainer = false,
  pageIsActive = true,
  tableProps,
  height,
  minusHeight,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { type = context || AppContext.CUSTOMER } = useParams();
  const { isMobile } = useScreen();

  // Context-specific query parameters
  const initialFilters = [
    ...(state?.walletAddress
      ? [`walletAddress.address||$eq||${state?.walletAddress}`]
      : []),
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "confirmedDate,DESC",
    join: ["walletAddress", "customer"],
    filter: initialFilters,
  });

  const query = useQuery({
    queryFn: () => apiGetBillingScannedTransaction(params),
    queryKey: ["customer-blockchain-payments", JSON.stringify(params)],
    refetchInterval: 1000 * 60,
    enabled: !!params && pageIsActive,
  });
  const { data, isLoading, isFetching } = query;
  const apiData = data?.data?.data;

  const columns = useMemo(() => {
    return getColumns({
      context: type as AppContext,
      apiData: apiData as ScannedTransaction[],
      params,
      messages,
      navigate,
      columnsConfig: tableProps?.columnsConfig,
      isMobile,
    });
  }, [apiData, params, type]);

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !["customerId"].includes(f.key)) as TableHeaderFilter[];
  }, [params, state]);

  // Context-specific title and breadcrumb
  const title =
    customTitle || (messages["common.blockchainPayments"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={withoutContainer}
      // height={height}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={query}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          tableProps={tableProps}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          onChange={onChange}
          scroll={{ y: height }}
          minusHeight={minusHeight}
          {...tableProps}
        />
      </AppsContent>
    </AppsContainer>
  );
};

export default SharedBlockchainPaymentList;
